import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Order, Payment, PaymentItem } from '../types';
import { doc, setDoc, onSnapshot, collection, query, orderBy, limit, updateDoc, getDocs, where, startAfter, QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import { processReferralCommission } from '../utils/referralCommission';

interface PaymentContextType {
  orders: Order[];
  payments: Payment[];
  currentOrder: Order | null;
  isLoading: boolean;
  createOrder: (orderData: Partial<Order>) => Promise<string>;
  updateOrderStatus: (orderId: string, status: Order['status']) => Promise<void>;
  getOrderById: (orderId: string) => Order | undefined;
  getUserOrders: () => Promise<void>;
  loadMoreUserOrders: () => Promise<void>;
  getUserPayments: () => Promise<void>;
  clearCurrentOrder: () => void;
  hasMoreOrders: boolean;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};

export const PaymentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [ordersCursor, setOrdersCursor] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMoreOrders, setHasMoreOrders] = useState<boolean>(false);
  const ORDERS_PAGE_SIZE = 5;

  // Load user orders and payments when user changes
  useEffect(() => {
    if (currentUser) {
      getUserOrders();
      getUserPayments();
    } else {
      setOrders([]);
      setPayments([]);
      setCurrentOrder(null);
    }
  }, [currentUser]);

  const createOrder = async (orderData: Partial<Order>): Promise<string> => {
    if (!currentUser) {
      throw new Error('User must be authenticated to create an order');
    }

    try {
      const orderId = `ORDER_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Calculate expiration date (1 year from now for courses)
      const expiresAt = new Date();
      expiresAt.setFullYear(expiresAt.getFullYear() + 1);
      
      const newOrder: Order = {
        id: orderId,
        userId: currentUser.uid,
        items: orderData.items || [],
        total: orderData.total || 0,
        currency: orderData.currency || 'GHS',
        status: 'pending',
        paymentMethod: orderData.paymentMethod || 'card',
        paymentReference: orderData.paymentReference || '',
        referredBy: orderData.referredBy,
        shippingAddress: orderData.shippingAddress!,
        billingAddress: orderData.billingAddress,
        createdAt: new Date(),
        updatedAt: new Date(),
        expiresAt,
        notes: orderData.notes,
      };

      // Save order to Firestore in users/{userId}/orders/{orderId} subcollection
      const userOrdersRef = doc(db, 'users', currentUser.uid, 'orders', orderId);
      await setDoc(userOrdersRef, {
        ...newOrder,
        createdAt: new Date(),
        updatedAt: new Date(),
        expiresAt,
      });

      // Process referral commission if applicable - only after order is successfully saved
      if (newOrder.referredBy) {
        try {
          // Convert Order items to PaymentItem format
          const paymentItems = newOrder.items.map(item => ({
            type: item.type,
            id: item.id,
            name: item.name,
            amount: item.amount
          }));
          
          await processReferralCommission(
            newOrder.referredBy,
            orderId,
            newOrder.paymentReference || orderId,
            paymentItems,
            newOrder.total
          );
          
          console.log('Referral commission processed successfully');
        } catch (error) {
          console.error('Error processing referral commission:', error);
          // Don't fail the order creation if commission processing fails
        }
      }

      // Add to local state
      setOrders(prev => [newOrder, ...prev]);
      setCurrentOrder(newOrder);

      toast.success('Order created successfully');
      return orderId;
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to create order');
      throw error;
    }
  };

  const updateOrderStatus = async (orderId: string, status: Order['status']): Promise<void> => {
    if (!currentUser) {
      throw new Error('User must be authenticated to update an order');
    }

    try {
      const userOrderRef = doc(db, 'users', currentUser.uid, 'orders', orderId);
      await updateDoc(userOrderRef, {
        status,
        updatedAt: new Date(),
        ...(status === 'completed' && { completedAt: new Date() }),
      });

      // Update local state
      setOrders(prev => 
        prev.map(order => 
          order.id === orderId 
            ? { ...order, status, updatedAt: new Date() }
            : order
        )
      );

      if (currentOrder?.id === orderId) {
        setCurrentOrder(prev => prev ? { ...prev, status, updatedAt: new Date() } : null);
      }

      toast.success(`Order status updated to ${status}`);
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status');
      throw error;
    }
  };

  const getOrderById = (orderId: string): Order | undefined => {
    return orders.find(order => order.id === orderId);
  };

  const getUserOrders = async (): Promise<void> => {
    if (!currentUser) return;

    try {
      setIsLoading(true);
      const userOrdersRef = collection(db, 'users', currentUser.uid, 'orders');
      const q = query(
        userOrdersRef,
        orderBy('createdAt', 'desc'),
        limit(ORDERS_PAGE_SIZE)
      );
      
      const querySnapshot = await getDocs(q);
      const userOrders: Order[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        userOrders.push({
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          completedAt: data.completedAt?.toDate(),
          expiresAt: data.expiresAt?.toDate(),
        } as Order);
      });

      setOrders(userOrders);
      const last = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
      setOrdersCursor(last);
      setHasMoreOrders(querySnapshot.size === ORDERS_PAGE_SIZE);
    } catch (error) {
      console.error('Error fetching user orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreUserOrders = async (): Promise<void> => {
    if (!currentUser || !ordersCursor || !hasMoreOrders) return;
    try {
      setIsLoading(true);
      const userOrdersRef = collection(db, 'users', currentUser.uid, 'orders');
      const q = query(
        userOrdersRef,
        orderBy('createdAt', 'desc'),
        startAfter(ordersCursor),
        limit(ORDERS_PAGE_SIZE)
      );
      const querySnapshot = await getDocs(q);
      const moreOrders: Order[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        moreOrders.push({
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          completedAt: data.completedAt?.toDate(),
          expiresAt: data.expiresAt?.toDate(),
        } as Order);
      });
      setOrders((prev) => [...prev, ...moreOrders]);
      const last = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
      setOrdersCursor(last);
      setHasMoreOrders(querySnapshot.size === ORDERS_PAGE_SIZE);
    } catch (error) {
      console.error('Error loading more orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getUserPayments = async (): Promise<void> => {
    if (!currentUser) return;

    try {
      const paymentsRef = collection(db, 'payments');
      const q = query(
        paymentsRef,
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const userPayments: Payment[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        userPayments.push({
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          completedAt: data.completedAt?.toDate(),
        } as Payment);
      });

      setPayments(userPayments);
    } catch (error) {
      console.error('Error fetching user payments:', error);
      // Don't show toast for payments as they're not critical for main functionality
    }
  };

  const clearCurrentOrder = () => {
    setCurrentOrder(null);
  };

  const value: PaymentContextType = {
    orders,
    payments,
    currentOrder,
    isLoading,
    createOrder,
    updateOrderStatus,
    getOrderById,
    getUserOrders,
    loadMoreUserOrders,
    getUserPayments,
    clearCurrentOrder,
    hasMoreOrders,
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};
